// FigureTile — one honest figure: value + label + optional provenance key +
// per-tile freshness dot (GAP-7 fallback to card-level) + low-n disclaim +
// optional threshold band + optional breakdown. No card layout — a composable
// atom.
//
// ME-CARDS-R1 A5: restyled to the locked prototype's tile (value FIRST in large
// type, label under it, the cited signal key in mono beneath) and given the
// tolerance-band gauge for the one figure that carries a `threshold`. No data
// logic changed — every value still comes through `resolveCoverage`.
import React from "react";

import type { Figure, Freshness } from "../../../chatBot/models/machineEvidenceModels";
import {
  isLowSample,
  resolveBreakdown,
  resolveCoverage,
  resolveFigureFreshness,
} from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";
import { FRESHNESS_COLORS } from "./honestyTokens";
import { CoverageValue } from "./CoverageValue";
import { Breakdown } from "./Breakdown";
import { ThresholdBand } from "./ThresholdBand";

export interface FigureTileProps {
  figure: Figure;
  /** card-level freshness — the per-tile dot falls back to this (GAP-7). */
  cardFreshness: Freshness | null | undefined;
  /** show the grouped breakdown under the value. */
  showBreakdown?: boolean;
  /** display the figure's `measure_basis` (commanded/measured/…) when present.
   *  Null on MASH (GAP-11); shown for SBL500 live-PLC pips. Rendered VERBATIM as a
   *  technical basis token (a closed contract enum), not translated. */
  showMeasureBasis?: boolean;
  /** the cited signal key (GUID-guarded by the caller) — rendered in mono under
   *  the label, per the prototype tile. Omitted when the caller has none. */
  signalKey?: string | null;
}

export const FigureTile: React.FC<FigureTileProps> = ({
  figure,
  cardFreshness,
  showBreakdown,
  showMeasureBasis,
  signalKey,
}) => {
  const { translate } = useTranslation("machineEvidence");
  const coverage = resolveCoverage(figure);
  const freshness = resolveFigureFreshness(figure, cardFreshness);
  const dotTone = FRESHNESS_COLORS[freshness.state];
  const lowN = isLowSample(figure.n);
  const breakdown = showBreakdown ? resolveBreakdown(figure.breakdown) : null;

  return (
    <div
      className="msc-tile"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: 10,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
        background: dt.cardBg,
      }}
    >
      <div style={{ fontSize: 20, lineHeight: 1.15 }}>
        <CoverageValue
          resolution={coverage}
          unit={figure.unit}
          denominator={figure.denominator}
          dim={freshness.dim}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          aria-hidden
          title={`Feed: ${freshness.state}`}
          style={{ width: 6, height: 6, borderRadius: "50%", background: dotTone.fg, flex: "0 0 auto" }}
        />
        <span style={{ fontSize: 12, color: dt.textSecondary }}>{figure.label}</span>
        {showMeasureBasis && figure.measure_basis ? (
          <span
            className="msc-measure-basis"
            style={{
              fontFamily: dt.fontMono,
              fontSize: 10,
              color: dt.textMuted,
              padding: "0 5px",
              borderRadius: dt.radiusPill,
              border: `1px solid ${dt.cardBorder}`,
            }}
          >
            {figure.measure_basis}
          </span>
        ) : null}
      </div>
      {signalKey ? (
        <span className="msc-tile-key" style={{ fontFamily: dt.fontMono, fontSize: 10, color: dt.textMuted }}>
          {signalKey}
        </span>
      ) : null}
      {/* A5: the tolerance gauge — renders only for a figure carrying a threshold. */}
      <ThresholdBand figure={figure} dim={freshness.dim} />
      {lowN ? (
        <span style={{ fontSize: 11, color: dt.textMuted, fontStyle: "italic" }}>
          {translate("figure.lowSample", {
            n: figure.n,
            defaultValue: "low sample (n={{n}})",
          }) as string}
        </span>
      ) : null}
      {breakdown ? <Breakdown resolution={breakdown} /> : null}
    </div>
  );
};

export default FigureTile;
